import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  DashboardCard,
  DashboardTableWrapper,
  DashboardTable,
  DashboardTableHeader,
  DashboardTableBody,
  DashboardTableRow,
} from '../PremiumDesign';
import { IntegrationHealthBadge } from './IntegrationHealthBadge';

// --- IntegrationLifecycleBadge ---
export function IntegrationLifecycleBadge({
  status,
  statusLabel,
  isConnected = false,
}) {
  const lifecycleLower = status?.toLowerCase() || '';
  const isLifecycleActivated = lifecycleLower === 'activated';
  const label =
    statusLabel ||
    status ||
    (isConnected ? 'Account Activated' : 'Pending Setup');

  return (
    <span
      className={`inline-block px-3 py-1 text-[11px] font-semibold rounded ${
        isLifecycleActivated
          ? 'text-[#4CAF50] bg-[#E8F5E9]'
          : 'text-amber-600 bg-amber-50'
      }`}
    >
      {label}
    </span>
  );
}

// --- IntegrationAlertBanner ---
export function IntegrationAlertBanner({ type = 'warning', title, message }) {
  if (!message) return null;

  const styles = {
    warning: 'bg-amber-50 border-amber-100 text-amber-700',
    error: 'bg-rose-50 border-rose-200 text-rose-700',
    info: 'bg-blue-50 border-blue-200 text-blue-700',
  };

  const icons = {
    warning: (
      <svg
        className="w-4 h-4 text-amber-500 shrink-0 mt-0.5"
        fill="none"
        viewBox="0 0 24 24"
        stroke="currentColor"
        strokeWidth="2"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          d="M12 9v2m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z"
        />
      </svg>
    ),
    error: (
      <svg
        className="w-4 h-4 text-rose-500 shrink-0 mt-0.5"
        fill="none"
        viewBox="0 0 24 24"
        stroke="currentColor"
        strokeWidth="2"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
        />
      </svg>
    ),
    info: (
      <svg
        className="w-4 h-4 text-blue-500 shrink-0 mt-0.5"
        fill="none"
        viewBox="0 0 24 24"
        stroke="currentColor"
        strokeWidth="2"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
        />
      </svg>
    ),
  };

  return (
    <div
      className={`p-3 rounded-lg border text-xs flex items-start gap-2 leading-relaxed ${styles[type] || styles.warning}`}
    >
      {icons[type] || icons.warning}
      <div>
        {title && (
          <strong className="font-semibold block mb-0.5">{title}</strong>
        )}
        <span>{message}</span>
      </div>
    </div>
  );
}

// --- IntegrationActionButtons ---
export function IntegrationActionButtons({
  isConnected,
  isHealthy,
  isConnecting,
  onConnect,
  onDisconnect,
  connectLabel = 'Connect',
  connectIcon = null,
  reconnectLabel = 'Reconnect Now',
  disconnectLabel = 'Disconnect',
  primaryColorClass = 'bg-primary hover:bg-primary/90 text-white',
}) {
  return (
    <div className="flex items-center gap-2 mt-2 pt-3 border-t border-gray-100 flex-wrap">
      {isConnected ? (
        <>
          <button
            onClick={onConnect}
            disabled={isConnecting}
            className={`w-max text-[13px] font-semibold py-2 px-4 rounded-md flex items-center gap-2 transition-colors shadow-sm ${
              !isHealthy
                ? 'bg-rose-600 hover:bg-rose-700 text-white'
                : primaryColorClass
            }`}
          >
            {isConnecting ? (
              <svg
                className="w-4 h-4 animate-spin"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="3"
              >
                <path d="M12 3a9 9 0 0 1 9 9" opacity="0.3" />
                <path d="M12 3a9 9 0 0 1 9 9" />
              </svg>
            ) : (
              <svg
                className="w-4 h-4"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                />
              </svg>
            )}
            <span>
              {isConnecting
                ? 'Redirecting...'
                : !isHealthy
                  ? reconnectLabel
                  : 'Reconnect'}
            </span>
          </button>

          {onDisconnect && (
            <button
              onClick={onDisconnect}
              className="w-max bg-red-50 hover:bg-red-100 text-red-600 text-[13px] font-medium py-2 px-3 rounded-md transition-colors"
            >
              {disconnectLabel}
            </button>
          )}
        </>
      ) : (
        <button
          onClick={onConnect}
          disabled={isConnecting}
          className={`w-full flex items-center justify-center gap-2 py-2.5 px-4 text-[13px] font-semibold transition-colors disabled:opacity-60 shadow-sm rounded-lg ${primaryColorClass}`}
        >
          {isConnecting ? (
            <>
              <svg
                className="w-3.5 h-3.5 animate-spin"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="3"
              >
                <path d="M12 3a9 9 0 0 1 9 9" opacity="0.3" />
                <path d="M12 3a9 9 0 0 1 9 9" />
              </svg>
              Redirecting...
            </>
          ) : (
            <>
              {connectIcon}
              {connectLabel}
            </>
          )}
        </button>
      )}
    </div>
  );
}

// --- IntegrationPermissionList ---
export function IntegrationPermissionList({ scopes = [], renderScope }) {
  if (!scopes || scopes.length === 0) return null;
  return (
    <DashboardCard className="p-5 shadow-sm border border-gray-100 rounded-[16px] bg-white flex flex-col gap-4">
      <div className="flex items-center justify-between">
        <h3 className="text-[13px] font-semibold text-gray-700 uppercase tracking-wide">
          Active Permissions
        </h3>
        <span className="text-[11px] text-gray-400">
          {scopes.length} scope{scopes.length !== 1 ? 's' : ''} granted
        </span>
      </div>
      <div className="flex flex-wrap gap-2">
        {scopes.map((scope) => (
          <React.Fragment key={scope}>{renderScope(scope)}</React.Fragment>
        ))}
      </div>
    </DashboardCard>
  );
}

// --- IntegrationStatusTable ---
export function IntegrationStatusTable({
  healthList = [],
  appId,
  onOpenDrawer,
  onOpenApp,
}) {
  const navigate = useNavigate();

  return (
    <DashboardTableWrapper>
      <DashboardTable className="w-full">
        <DashboardTableHeader>
          <th className="py-3 px-6 text-left font-medium">Integration Name</th>
          <th className="py-3 px-6 text-left font-medium">Integration Slug</th>
          <th className="py-3 px-6 text-center font-medium">
            Lifecycle Status
          </th>
          <th className="py-3 px-6 text-center font-medium">Health Status</th>
          <th className="py-3 px-6 text-center font-medium w-24">Action</th>
        </DashboardTableHeader>
        <DashboardTableBody>
          {healthList.length === 0 ? (
            <tr>
              <td
                colSpan="5"
                className="py-16 text-center text-text-muted border-b border-gray-100"
              >
                <span className="text-[13px] font-medium">
                  No integrations found for this application.
                </span>
              </td>
            </tr>
          ) : (
            healthList.map((item, idx) => {
              const healthLower = item.health_status?.toLowerCase() || '';
              const isHealthy =
                ['healthy', 'connected', 'activated'].includes(healthLower) ||
                item.health_status == null;

              const historyPath = appId
                ? `/integrations/${appId}/history`
                : `/integrations/${item.integration_slug}/history`;

              return (
                <DashboardTableRow
                  key={item.id || idx}
                  className="border-b border-gray-50 last:border-none"
                >
                  <td className="py-4 px-6 text-[13px] text-gray-900 font-medium">
                    {item.integration_name}
                  </td>
                  <td className="py-4 px-6 text-[13px] text-gray-900">
                    {item.integration_slug}
                  </td>
                  <td className="py-4 px-6 text-center">
                    <IntegrationLifecycleBadge
                      status={item.lifecycle_status}
                      statusLabel={item.lifecycle_status_label}
                      isConnected={
                        item.health_status !== 'disconnected' &&
                        item.health_status != null
                      }
                    />
                  </td>
                  <td className="py-4 px-6 text-center">
                    <IntegrationHealthBadge
                      status={item.health_status}
                      statusLabel={item.health_status_label}
                      showFailureReason={false}
                      isConnected={
                        item.health_status !== 'disconnected' &&
                        item.health_status != null
                      }
                    />
                  </td>
                  <td
                    className="py-4 px-6 text-center"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <div className="flex items-center justify-center gap-3">
                      {/* Health History */}
                      <button
                        onClick={() => navigate(historyPath)}
                        title="View Health History Logs"
                        className="text-orange-500 hover:opacity-70 transition-opacity p-1 rounded hover:bg-orange-50"
                      >
                        <svg
                          className="w-[18px] h-[18px]"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                          strokeWidth="2"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                          />
                        </svg>
                      </button>

                      {/* Chat / Open Action */}
                      <button
                        disabled={!isHealthy}
                        onClick={() => onOpenApp && onOpenApp(item)}
                        title={
                          isHealthy
                            ? 'Open Chat / App'
                            : 'Connection must be healthy before this action can be used.'
                        }
                        className={`transition-opacity p-1 rounded ${
                          !isHealthy
                            ? 'text-gray-400 cursor-not-allowed'
                            : 'text-blue-500 hover:opacity-70 hover:bg-blue-50'
                        }`}
                      >
                        <svg
                          className="w-[18px] h-[18px]"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                          strokeWidth="2"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            d="M17 8h2a2 2 0 012 2v6a2 2 0 01-2 2h-2v4l-4-4H9a1.994 1.994 0 01-1.414-.586m0 0L11 14h4a2 2 0 002-2V6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2v4l.586-.586z"
                          />
                        </svg>
                      </button>

                      {/* Request Drawer */}
                      <button
                        disabled={isHealthy}
                        onClick={() => onOpenDrawer && onOpenDrawer()}
                        title={
                          isHealthy
                            ? 'Integration is Healthy'
                            : 'View / Submit Reconnect Request'
                        }
                        className={`transition-opacity p-1 rounded ${
                          isHealthy
                            ? 'text-purple-500 opacity-40 cursor-not-allowed'
                            : 'text-purple-500 hover:opacity-70 hover:bg-purple-50'
                        }`}
                      >
                        <svg
                          className="w-[18px] h-[18px]"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                          strokeWidth="2"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z"
                          />
                        </svg>
                      </button>
                    </div>
                  </td>
                </DashboardTableRow>
              );
            })
          )}
        </DashboardTableBody>
      </DashboardTable>
    </DashboardTableWrapper>
  );
}
